import { AuthenticationService } from '@anihis/shared/src/services/authentication.service';
import { StorageService } from '@anihis/shared/src/services/storage.service';
import { StorageKeyConstants } from '@anihis/shared/src/constants/storage-key-constants';
import {
  Component,
  HostBinding,
  inject,
  ViewChild,
  signal,
  OnInit,
} from '@angular/core';
import { environment } from '../../environments/environment';
import { of, from } from 'rxjs';
import { catchError, switchMap, finalize, map } from 'rxjs/operators';
import { CommonModule } from '@angular/common';

import { RouterModule } from '@angular/router';
import { NavigationComponent } from './navigation/navigation.component';

import { CATEGORY_TREE } from './navigate-menu';
import { TabsStateService } from '../shared/services/tabs-state.service';
import { AnimalsService, ClinicPremiumBillingsService, MedicinesService } from '@anihis/core/data-access';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OwnerCardStateService } from '../shared/services/owner-card-state.service';
import { ShellStore } from './shell.store';
import { TranslocoModule } from '@jsverse/transloco';
import { PremiumInvoicesBadgeService } from '../settings/premium-invoices/premium-invoices-badge.service';
import { SettingsNavigationService } from '../settings/settings-navigation.service';
import { tabLabelKey } from '../shared/config/manager-portal-tabs.config';
export interface Category {
  name: string;
  children?: Category[];
  action?: string;
  icon?: string;
  open?: boolean;
  roles?: string[];
}

@Component({
  selector: 'anihis-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NavigationComponent,
    MatExpansionModule,
    MatProgressSpinnerModule,
    TranslocoModule,
  ],
  providers: [
    AnimalsService,
    MedicinesService,
    ClinicPremiumBillingsService,
  ],
})
export class ShellComponent implements OnInit {
  readonly showEnvironmentBanner = environment.showEnvironmentBanner ?? false;

  @HostBinding('class.has-environment-banner')
  get hasEnvironmentBannerClass(): boolean {
    return this.showEnvironmentBanner;
  }

  @ViewChild('navigation') navigationComponent!: NavigationComponent;
  private authenticationService = inject(AuthenticationService);
  private storageService = inject(StorageService);
  private ownerCardStateService = inject(OwnerCardStateService);
  private tabsStateService = inject(TabsStateService);
  private settingsNavigation = inject(SettingsNavigationService);
  premiumBadge = inject(PremiumInvoicesBadgeService);
  tabs = this.tabsStateService.tabs;
  readonly tabLabelKey = tabLabelKey;

  CATEGORY_TREE: Category[] = CATEGORY_TREE;
  userRole = this.authenticationService.getRoleFromToken();

  isOffCanvasMenu = false;
  isOffCanvasMenuDialog = false;
  filteredCategoryTree!: any[];
  isLoggingOut = signal(false);
  sidebarOpen = signal(false);
  notificationsOpen = signal(false);

  toggleSidebar() {
    this.sidebarOpen.update((v) => !v);
  }

  closeSidebar() {
    this.sidebarOpen.set(false);
  }

  toggleNotifications() {
    const opening = !this.notificationsOpen();
    this.notificationsOpen.set(opening);
    if (opening) {
      this.store.markAllRead();
    }
  }

  store = inject(ShellStore);
  notifications: Array<{ text: string; createdAt?: string }> = [];

  ngOnInit(): void {
    if (this.showEnvironmentBanner && environment.shellTheme) {
      const theme = environment.shellTheme;
      document.documentElement.style.setProperty('--primary-color', theme.primary);
      document.documentElement.style.setProperty('--shell-primary', theme.primary);
      document.documentElement.style.setProperty(
        '--shell-primary-dark',
        theme.primaryDark,
      );
      document.documentElement.style.setProperty(
        '--shell-primary-border',
        theme.primaryBorder,
      );
    }

    // Re-check on every shell entry (e.g. after login) so new clinics/owners
    // always see the latest published legal documents they have not accepted yet.
    this.store.checkPendingLegalDocuments();
  }

  constructor() {
    this.filterCategoryTreeByRole();

    // Safely parse stored tabs and add them individually
    let storedTabs: string[] = [];
    of(localStorage.getItem('tabs') || '[]')
      .pipe(
        map((s) => JSON.parse(s)),
        catchError(() => of([])),
      )
      .subscribe((r) => (storedTabs = Array.isArray(r) ? r : []));

    storedTabs.forEach((t) => this.tabsStateService.addTab(t));
    // Restore previously selected tab index from localStorage (don't reset to 0)
    const savedIndex = localStorage.getItem('tabs-selected-index');
    const parsedIndex = savedIndex !== null ? parseInt(savedIndex, 10) : 0;
    this.tabsStateService.updateState(isNaN(parsedIndex) ? 0 : parsedIndex);
  }

  isPanelOpen(index: number): boolean {
    // For regular users - first panel open
    // For Administrator - handled in template with [expanded]="true"
    return index === 0;
  }

  redirectTo(event: MouseEvent, tabName: string) {
    event.stopPropagation();
    this.closeSidebar();
    this.ownerCardStateService.clearState();
    localStorage.removeItem('ownerUid');
    localStorage.removeItem('animalUid');

    if (
      tabName === 'Manager sub-managers' &&
      !this.store.canManageManagerSubordinates()
    ) {
      this.tabsStateService.createOpenTabs('Dashboard');
      setTimeout(() => {
        this.navigationComponent?.scrollToActiveTab();
      }, 200);
      return;
    }

    this.tabsStateService.createOpenTabs(tabName);

    setTimeout(() => {
      if (this.navigationComponent) {
        this.navigationComponent.scrollToActiveTab();
      }
    }, 200);
  }

  redirectToSettings(event: MouseEvent) {
    event.stopPropagation();
    this.closeSidebar();
    this.tabsStateService.createOpenTabs('Settings');
    setTimeout(() => {
      this.navigationComponent?.scrollToActiveTab();
    }, 200);
  }

  redirectToPremiumInvoices(event: MouseEvent) {
    event.stopPropagation();
    this.closeSidebar();
    this.settingsNavigation.openPremiumInvoices();
    this.tabsStateService.createOpenTabs('Settings');
    setTimeout(() => {
      this.navigationComponent?.scrollToActiveTab();
    }, 200);
  }

  redirectToQrCode(event: MouseEvent) {
    event.stopPropagation();

    // this.tabsComponentService.createOpenTabs('QR Code');
    // this.applicationStateService.isOpenMenu(false);
    // this.isShowSettingsSubject.next(true);
  }

  // openVeterinariansDialog(event: MouseEvent) {
  //   event.stopPropagation();
  //   this.isShowSettingsSubject.next(true);

  //   const dialogRef = this.dialog.open(VeterinarSelectionDialogComponent, {
  //
  //     width: '946px',
  //     autoFocus: false,
  //   });
  //   dialogRef.afterClosed().subscribe((result) => {
  //     if (result) {
  //       // this.cardsService.editOwner(result);
  //     }
  //   });
  // }

  logOut(event: MouseEvent) {
    event.stopPropagation();
    this.isLoggingOut.set(true);

    const getLang$ = from(
      this.storageService.getItem<string | undefined>(
        StorageKeyConstants.LANGUAGE_KEY,
      ),
    ).pipe(catchError(() => of(undefined)));

    getLang$
      .pipe(
        switchMap((storedLang) => {
          let lang = storedLang;
          if (!lang && typeof localStorage !== 'undefined') {
            lang = localStorage.getItem('lang') ?? undefined;
          }

          // 1. Call logout API first (needs token in storage)
          return this.store.logout().pipe(
            catchError(() => of(null)),
            switchMap(() => {
              // 2. Only after API call — clear auth-only data while preserving theme/lang
              localStorage.removeItem('tabs');
              localStorage.removeItem('selectedClinicUid');
              return from(this.storageService.clearAuthData()).pipe(
                catchError(() => {
                  if (typeof localStorage !== 'undefined') {
                    localStorage.clear();
                  }
                  return of(null);
                }),
                switchMap(() =>
                  lang
                    ? from(
                        this.storageService.setItem(
                          StorageKeyConstants.LANGUAGE_KEY,
                          lang,
                        ),
                      ).pipe(catchError(() => of(null)))
                    : of(null),
                ),
              );
            }),
          );
        }),
        finalize(() => window.location.reload()),
      )
      .subscribe();
  }

  filterCategoryTreeByRole() {
    this.filteredCategoryTree = JSON.parse(JSON.stringify(CATEGORY_TREE));

    this.filteredCategoryTree = this.filteredCategoryTree.filter((category) => {
      if (this.userRole === 'Manager') {
        return false;
      }

      if (this.userRole === 'ClinicOwner') {
        if (category.name !== 'Coders') {
          return false;
        }
      } else if (this.userRole === 'Administrator') {
        if (
          !(
            category.name === 'Coders' &&
            category.name === 'Protocols' &&
            category.name === 'Dashboard'
          )
        ) {
          return false;
        }
      }

      category.children = category.children?.filter((child: Category) => {
        if (child.roles && !child.roles.includes(this.userRole!)) {
          return false;
        }
        if (this.userRole === 'Clinic') {
          return (
            child.name !== 'Veterinarians' &&
            child.name !== 'Clinics' &&
            child.name !== 'Trainers' &&
            child.name !== 'Groomers' &&
            child.name !== 'Pet Transporters'
          );
        } else if (this.userRole === 'ClinicOwner') {
          return (
            child.name !== 'Clinics' &&
            child.name !== 'Trainers' &&
            child.name !== 'Groomers' &&
            child.name !== 'Pet Transporters' &&
            child.name !== 'Owners' &&
            child.name !== 'Cards' &&
            child.name !== 'Serial number confirmation'
          );
        } else if (this.userRole === 'Administrator') {
          return (
            child.name === 'Breeds' ||
            child.name === 'Clinics' ||
            child.name === 'Trainers' ||
            child.name === 'Groomers' ||
            child.name === 'Pet Transporters' ||
            child.name === 'Subscription Plans'
          );
        } else if (this.userRole === 'Veterinarian') {
          return (
            child.name !== 'Veterinarians' &&
            child.name !== 'Clinics' &&
            child.name !== 'Trainers' &&
            child.name !== 'Groomers' &&
            child.name !== 'Pet Transporters'
          );
        }
        return true;
      });

      return true;
    });
  }

  toggleOffCanvasMenu() {
    this.isOffCanvasMenu = !this.isOffCanvasMenu;

    if (this.isOffCanvasMenuDialog) {
      setTimeout(() => {
        this.isOffCanvasMenuDialog = !this.isOffCanvasMenuDialog;
      }, 400);
    } else {
      this.isOffCanvasMenuDialog = !this.isOffCanvasMenuDialog;
    }
  }

  // async changeLanguage(language: string) {
  //   this.translationsService.changeLanguage(language);
  //   this.getLanguage = language;
  //   // this.flagPath = this.changeFlag(language);
  // }

  // onThemeChange(theme: string): void {
  //   const themeProperties = themes[theme];
  //   if (themeProperties) {
  //     Object.entries(themeProperties).forEach(([property, value]) => {
  //       document.documentElement.style.setProperty(property, value);
  //     });
  //   }
  //   this.getTheme = theme;
  // }
}
